import { AxiosResponse } from "axios";
import api from "../Interceptors/Interceptor";
import ApiResponse from "../Models/ApiResponse";

export const MEDIA_TYPE = {
    VIDEO: "VIDEO",
    PHOTO: "PHOTO",
    FILE: "FILE",
} as const;

export type MediaType = (typeof MEDIA_TYPE)[keyof typeof MEDIA_TYPE];

/** Matches nl.im.dashly.backend.constants.MediaVariantType — the rendition ladder of
 *  MediaStorageServiceImpl#startVideoTranscode. Which of these a video actually has is in its
 *  `renditions`: the backend never upscales, and there are none while the video is still processing. */
export const VIDEO_RESOLUTION_OPTIONS: { label: string; variantType: string; height: number }[] = [
    { label: "1080p", variantType: "RENDITION_1080P", height: 1080 },
    { label: "720p", variantType: "RENDITION_720P", height: 720 },
    { label: "480p", variantType: "RENDITION_480P", height: 480 },
    { label: "360p", variantType: "RENDITION_360P", height: 360 },
];

/** Used only when the browser exposes neither `navigator.connection.downlink` nor a usable speed probe. */
export const VIDEO_QUALITY_FALLBACK_HEIGHT = 720;

/** Starting quality vs estimated Mbps. Thresholds follow Chrome's Network Information `downlink`
 *  buckets and sit at roughly 2× the transcoded video bitrate (see MediaStorageServiceImpl renditions)
 *  so playback has headroom instead of saturating the link. Auto-select never targets the original
 *  file — 1080p is the ceiling; `pickDefaultVideoQuality` then snaps to the best available size at or
 *  below that target. */
export const VIDEO_NETWORK_QUALITY_LADDER: { minMbps: number; height: number }[] = [
    { minMbps: 10, height: 1080 },
    { minMbps: 5, height: 720 },
    { minMbps: 2.5, height: 480 },
    { minMbps: 0, height: 360 },
];

const SPEED_PROBE_BYTES = 262144;

interface NetworkInformationLike {
    downlink?: number;
    effectiveType?: string;
    saveData?: boolean;
}

/** Highest size that does not exceed `preferredHeight`, or the smallest available size when every
 *  rendition is taller (e.g. only the original exists). Without a preferred height, returns the highest. */
export function pickDefaultVideoQuality(sizes: number[], preferredHeight?: number): number {
    const uniqueSorted = Array.from(new Set(sizes)).sort((a, b) => b - a);
    if (uniqueSorted.length === 0) {
        return preferredHeight ?? 0;
    }
    if (preferredHeight === undefined) {
        return uniqueSorted[0];
    }
    const atOrBelowPreferred = uniqueSorted.filter((size) => size <= preferredHeight);
    return atOrBelowPreferred[0] ?? uniqueSorted[uniqueSorted.length - 1];
}

export function preferredVideoHeightForMbps(mbps?: number): number {
    if (mbps === undefined) {
        return VIDEO_QUALITY_FALLBACK_HEIGHT;
    }
    const match = VIDEO_NETWORK_QUALITY_LADDER.find((rung) => mbps >= rung.minMbps);
    return match?.height ?? VIDEO_QUALITY_FALLBACK_HEIGHT;
}

const getNetworkInformation = (): NetworkInformationLike | undefined => {
    const nav = navigator as Navigator & {
        connection?: NetworkInformationLike;
        mozConnection?: NetworkInformationLike;
        webkitConnection?: NetworkInformationLike;
    };
    return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
};

const effectiveTypeToMbps = (effectiveType?: string): number | undefined => {
    switch (effectiveType) {
        case "slow-2g":
        case "2g":
            return 0.4;
        case "3g":
            return 1.5;
        case "4g":
            return 5;
        default:
            return undefined;
    }
};

const measureMbpsFromUrl = async (url: string): Promise<number | undefined> => {
    const controller = new AbortController();
    const startedAt = performance.now();
    try {
        const response = await fetch(url, {
            headers: { Range: `bytes=0-${SPEED_PROBE_BYTES - 1}` },
            cache: "no-store",
            signal: controller.signal,
        });
        if (!response.ok && response.status !== 206) {
            controller.abort();
            return undefined;
        }
        const buffer = await response.arrayBuffer();
        const elapsedSeconds = (performance.now() - startedAt) / 1000;
        if (elapsedSeconds <= 0 || buffer.byteLength === 0) {
            return undefined;
        }
        return (buffer.byteLength * 8) / (elapsedSeconds * 1_000_000);
    } catch {
        controller.abort();
        return undefined;
    }
};

/** Browser-reported downlink (Network Information API) when present; otherwise a short Range probe
 *  against `probeUrl`, then `effectiveType`, then undefined so the caller can fall back to 720p. */
export const estimateNetworkMbps = async (probeUrl?: string): Promise<number | undefined> => {
    const connection = getNetworkInformation();
    if (connection?.saveData) {
        return 0;
    }
    if (typeof connection?.downlink === "number" && connection.downlink > 0) {
        return connection.downlink;
    }
    if (probeUrl) {
        const probed = await measureMbpsFromUrl(probeUrl);
        if (probed !== undefined) {
            return probed;
        }
    }
    return effectiveTypeToMbps(connection?.effectiveType);
};

/** Picks the starting playback height from the current network estimate, snapped to an available size. */
export const resolvePreferredVideoHeight = async (availableSizes: number[], probeUrl?: string): Promise<number> => {
    const mbps = await estimateNetworkMbps(probeUrl);
    return pickDefaultVideoQuality(availableSizes, preferredVideoHeightForMbps(mbps));
};

/** Matches knowledge-base.storage.max-video-size-bytes (1 GB). */
export const MAX_VIDEO_SIZE_BYTES = 1024 * 1024 * 1024;

/** Matches knowledge-base.storage.max-video-duration-seconds (5 minutes). */
export const MAX_VIDEO_DURATION_SECONDS = 300;

/** Matches knowledge-base.storage.max-photo-size-bytes (10 MB). */
export const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024;

/** Matches knowledge-base.storage.max-file-size-bytes (100 MB). */
export const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

export const VIDEO_TOO_LARGE_MESSAGE = "knowledgeBase.form.mediaVideoTooLarge";
export const VIDEO_TOO_LONG_MESSAGE = "knowledgeBase.form.mediaVideoTooLong";
export const PHOTO_TOO_LARGE_MESSAGE = "knowledgeBase.form.mediaPhotoTooLarge";
export const FILE_TOO_LARGE_MESSAGE = "knowledgeBase.form.mediaFileTooLarge";

export const VIDEO_POSTER_MAX_WIDTH = 1280;
export const VIDEO_POSTER_JPEG_QUALITY = 0.8;

export interface VideoFileMetadata {
    width?: number;
    height?: number;
    durationSeconds?: number;
    /** First usable frame as JPEG — sent with the upload so header/slideshow can show a still before play. */
    poster?: Blob;
}

/** Reads resolution and duration client-side (multipart metadata doesn't include either). When
 *  `capturePoster` is true, also seeks a short way in and draws a JPEG still from the same decode. */
export function readVideoMetadata(file: File, options?: { capturePoster?: boolean }): Promise<VideoFileMetadata> {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        const cleanup = () => URL.revokeObjectURL(url);
        const finish = (metadata: VideoFileMetadata) => {
            cleanup();
            resolve(metadata);
        };
        video.muted = true;
        video.playsInline = true;
        video.preload = "auto";
        video.onerror = () => finish({});
        video.onloadeddata = () => {
            const metadata: VideoFileMetadata = {
                width: video.videoWidth || undefined,
                height: video.videoHeight || undefined,
                durationSeconds: Number.isFinite(video.duration) ? video.duration : undefined,
            };
            if (!options?.capturePoster || !video.videoWidth) {
                finish(metadata);
                return;
            }
            const capture = () => {
                const scale = video.videoWidth > VIDEO_POSTER_MAX_WIDTH ? VIDEO_POSTER_MAX_WIDTH / video.videoWidth : 1;
                const canvas = document.createElement("canvas");
                canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
                canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
                const context = canvas.getContext("2d");
                if (!context) {
                    finish(metadata);
                    return;
                }
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(
                    (blob) => finish({ ...metadata, poster: blob ?? undefined }),
                    "image/jpeg",
                    VIDEO_POSTER_JPEG_QUALITY
                );
            };
            const seekTo = Number.isFinite(video.duration) && video.duration > 0.25 ? Math.min(1, video.duration * 0.1) : 0;
            if (seekTo === 0 || Math.abs(video.currentTime - seekTo) < 0.05) {
                capture();
                return;
            }
            video.onseeked = capture;
            video.currentTime = seekTo;
        };
        video.src = url;
    });
}

/** Returns a translation key when the video exceeds the size or duration cap; otherwise undefined. */
export async function videoUploadConstraintViolation(file: File): Promise<string | undefined> {
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
        return VIDEO_TOO_LARGE_MESSAGE;
    }
    const metadata = await readVideoMetadata(file);
    if (metadata.durationSeconds !== undefined && metadata.durationSeconds > MAX_VIDEO_DURATION_SECONDS) {
        return VIDEO_TOO_LONG_MESSAGE;
    }
    return undefined;
}

/** Size cap for a photo or a plain file. Videos go through {@link videoUploadConstraintViolation}. */
export function fileUploadConstraintViolation(file: File): string | undefined {
    if (looksLikePhotoFile(file)) {
        return file.size > MAX_PHOTO_SIZE_BYTES ? PHOTO_TOO_LARGE_MESSAGE : undefined;
    }
    return file.size > MAX_FILE_SIZE_BYTES ? FILE_TOO_LARGE_MESSAGE : undefined;
}

/** Splits the files a user picked into what can be uploaded and why the rest cannot (translation keys):
 *  a video, photo or file over its cap is refused up front, not after minutes of uploading. */
export async function checkUploadableFiles(files: File[]): Promise<{ accepted: File[]; violations: string[] }> {
    const accepted: File[] = [];
    const violations: string[] = [];
    for (const file of files) {
        const violation = looksLikeVideoFile(file)
            ? await videoUploadConstraintViolation(file)
            : fileUploadConstraintViolation(file);
        if (violation) {
            violations.push(violation);
        } else {
            accepted.push(file);
        }
    }
    return { accepted, violations };
}

/**
 * Upload (write) goes through the backend as a normal multipart POST — no signed-upload-URL detour, see
 * knowledge-base-media-storage-plan-2026-08-24.md §3.4. Reading (playback/download) only ever fetches a
 * short-lived signed URL; the browser talks to the bucket directly from there on, never through this API.
 */
export const IMAGE_ATTACHMENT_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"] as const;

export const isImageAttachment = (asset: { mimeType?: string; originalFilename?: string }): boolean => {
    if (asset.mimeType?.toLowerCase().startsWith("image/")) {
        return true;
    }
    const name = (asset.originalFilename ?? "").toLowerCase();
    return IMAGE_ATTACHMENT_EXTENSIONS.some((extension) => name.endsWith(extension));
};

/** Matches the backend's own extension list (MediaAssetServiceImpl#isVideoUpload) — browsers frequently
 *  report a blank/wrong MIME type for less common containers like `.mkv`, so the filename is checked too. */
export const VIDEO_ATTACHMENT_EXTENSIONS = [
    ".mp4", ".mov", ".m4v", ".avi", ".wmv", ".flv", ".webm", ".mkv", ".mpg", ".mpeg", ".3gp", ".ogv",
] as const;

export const isVideoAttachment = (asset: { mimeType?: string; originalFilename?: string }): boolean => {
    if (asset.mimeType?.toLowerCase().startsWith("video/")) {
        return true;
    }
    const name = (asset.originalFilename ?? "").toLowerCase();
    return VIDEO_ATTACHMENT_EXTENSIONS.some((extension) => name.endsWith(extension));
};

/** Same detection, applied to a not-yet-uploaded `File` (browser `type`/`name` instead of the server's
 *  `mimeType`/`originalFilename`) — used to decide whether to capture video metadata/poster before upload,
 *  independent of what `mediaType` the caller happens to be sending. */
export const looksLikeVideoFile = (file: File): boolean => isVideoAttachment({ mimeType: file.type, originalFilename: file.name });

export const looksLikePhotoFile = (file: File): boolean => isImageAttachment({ mimeType: file.type, originalFilename: file.name });

export interface IMediaRenditionModel {
    variantType: string;
    url: string;
    /** This rendition's own resolution; undefined for one registered before it was recorded. */
    width?: number;
    height?: number;
}

/**
 * One media asset of an article, including the signed URLs a page needs to show it — they come with the
 * list (`fetchByArticle`), so showing an article costs one media request, not one per image. The URLs are
 * short-lived (an hour): a page that stays open reloads the list, see MEDIA_URL_REFRESH_MS.
 */
export interface IMediaAssetModel {
    uuid: string;
    mediaType: MediaType;
    /** The section this asset belongs to (IMAGE/VIDEO/SLIDESHOW item, or a TEXT section's inline image).
     *  Undefined only for a plain, article-level FILE attachment. */
    sectionUuid?: string;
    originalFilename: string;
    /** The original name's extension, lower case and without the dot; undefined when it had none. */
    fileExtension?: string;
    mimeType?: string;
    sizeBytes?: number;
    /** The original's own resolution: read from the image for a photo, measured by the browser for a video.
     *  Undefined when unknown (an SVG, or an asset uploaded before this was tracked). */
    sourceWidth?: number;
    sourceHeight?: number;
    /** A video's length in seconds; undefined for anything else. */
    sourceDurationSeconds?: number;
    processingStatus: "PENDING" | "PROCESSING" | "READY" | "FAILED";
    /** The uploaded file itself. Not set for a FILE attachment: that one is only ever downloaded. */
    originalUrl?: string;
    /** Small still: a photo's thumbnail, a video's poster. */
    thumbnailUrl?: string;
    /** Photo only: the display-size version. */
    reducedUrl?: string;
    /** Video only: the transcoded renditions that exist, lowest first. */
    renditions?: IMediaRenditionModel[];
}

/** Signed media URLs live for an hour (knowledge-base.storage.signed-url-ttl-minutes); a page that stays
 *  open reloads its media list well before that. */
export const MEDIA_URL_REFRESH_MS = 45 * 60 * 1000;

/** What to show for a photo: its display-size version, or the original when none was made (SVG). */
export const photoDisplayUrl = (asset: IMediaAssetModel): string | undefined => asset.reducedUrl ?? asset.originalUrl;

/** The shape of the media itself, for a page that wants to reserve its space before it loads. Undefined
 *  when the asset never got a resolution (an SVG, or anything uploaded before it was recorded). */
export const mediaAspectRatio = (asset: IMediaAssetModel): number | undefined =>
    asset.sourceWidth && asset.sourceHeight ? asset.sourceWidth / asset.sourceHeight : undefined;

/** "1920 x 1080", or undefined when the resolution is not known. */
export const formatMediaResolution = (asset: IMediaAssetModel): string | undefined =>
    asset.sourceWidth && asset.sourceHeight ? `${asset.sourceWidth} × ${asset.sourceHeight}` : undefined;

/** A video's length as m:ss (h:mm:ss past the hour); undefined for anything without one. */
export const formatMediaDuration = (asset: IMediaAssetModel): string | undefined => {
    const seconds = asset.sourceDurationSeconds;
    if (seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
        return undefined;
    }
    const whole = Math.round(seconds);
    const parts = [Math.floor(whole / 3600), Math.floor((whole % 3600) / 60), whole % 60];
    return parts[0] > 0
        ? `${parts[0]}:${String(parts[1]).padStart(2, "0")}:${String(parts[2]).padStart(2, "0")}`
        : `${parts[1]}:${String(parts[2]).padStart(2, "0")}`;
};

/** Small still for lists and pickers: a photo's thumbnail, a video's poster. Undefined for a plain file. */
export const mediaThumbnailUrl = (asset: IMediaAssetModel): string | undefined =>
    asset.thumbnailUrl ?? (asset.mediaType === MEDIA_TYPE.PHOTO ? photoDisplayUrl(asset) : undefined);

export class MediaAssetService {
    public async fetchByArticle(knowledgeArticleUuid: string): Promise<AxiosResponse<ApiResponse<IMediaAssetModel[]>>> {
        return api.get(`v1/media-asset/by-article/${knowledgeArticleUuid}`);
    }

    public async uploadMediaAsset(
        knowledgeArticleUuid: string,
        mediaType: MediaType,
        file: File,
        sectionUuid?: string
    ): Promise<AxiosResponse<ApiResponse<string>>> {
        const formData = new FormData();
        formData.append("knowledgeArticleUuid", knowledgeArticleUuid);
        formData.append("mediaType", mediaType);
        if (sectionUuid) {
            formData.append("sectionUuid", sectionUuid);
        }
        // Capture metadata/poster whenever the file itself looks like a video, not just when the caller
        // already declared VIDEO — the generic Attachments uploader always sends FILE and lets the backend
        // decide, so this is the only chance to grab a client-side poster/duration for those uploads too.
        if (mediaType === MEDIA_TYPE.VIDEO || looksLikeVideoFile(file)) {
            const metadata = await readVideoMetadata(file, { capturePoster: true });
            if (metadata.width !== undefined) {
                formData.append("sourceWidth", String(metadata.width));
            }
            if (metadata.height !== undefined) {
                formData.append("sourceHeight", String(metadata.height));
            }
            if (metadata.durationSeconds !== undefined) {
                formData.append("sourceDurationSeconds", String(metadata.durationSeconds));
            }
            if (metadata.poster) {
                formData.append("thumbnail", metadata.poster, "thumbnail.jpg");
            }
        }
        formData.append("file", file);
        return api.post("v1/media-asset", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    }

    /** A signed URL that makes the browser SAVE the file under its real name — for an explicit "Download"
     *  action only. Everything a page merely shows already has its URL in the media list. */
    public async fetchDownloadUrl(assetUuid: string, variantType?: string): Promise<AxiosResponse<ApiResponse<string>>> {
        return api.get(`v1/media-asset/${assetUuid}/download-url`, {
            params: { ...(variantType ? { variantType } : {}), download: true },
        });
    }

    /** Image stills and FILE originals for PDF/zip export. Not used for video files. */
    public async fetchContent(assetUuid: string, variantType?: string): Promise<Blob> {
        const response = await api.get<Blob>(`v1/media-asset/${assetUuid}/content`, {
            params: variantType ? { variantType } : {},
            responseType: "blob",
        });
        return response.data;
    }

    public async deleteMediaAsset(assetUuid: string): Promise<AxiosResponse<ApiResponse<void>>> {
        return api.delete(`v1/media-asset/${assetUuid}`);
    }

    /** Reassigns an already-uploaded PHOTO/VIDEO asset to a different section on the same article — e.g.
     *  picking an existing image for a slideshow instead of re-uploading it. */
    public async assignToSection(assetUuid: string, sectionUuid: string): Promise<AxiosResponse<ApiResponse<void>>> {
        return api.put(`v1/media-asset/${assetUuid}/section`, { sectionUuid });
    }
}
